import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, getAuth, updatePassword } from 'firebase/auth';
import PasswordUpdateSuccessModal from './PasswordUpdateSuccessModal';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChangePassword = async () => {
    setError('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        setError('No user is currently signed in');
        setLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      
      // FIXED: Clear form fields and show success modal WITHOUT calling onClose
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Password change error:', err);
      
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign in again before changing your password');
      } else if (err.code === 'auth/weak-password') {
        setError('New password is too weak. Please use a stronger password');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  // FIXED: Close success modal AND parent modal
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose(); // Now close the parent modal
  };

  // FIXED: Don't return null if parent is closed - check each modal individually
  return (
    <>
      {/* Change Password Modal */}
      {isOpen && !showSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop with whitish blur */}
          <div 
            className="absolute inset-0 bg-white/40 backdrop-blur-md"
            onClick={handleCancel}
          />
          
          {/* Modal Card */}
          <div className="relative bg-white rounded-2xl border-[1.5px] border-primary-blue shadow-2xl w-full max-w-[25rem] 2xl:max-w-md">
            {/* Header with centered title */}
            <div className="relative px-8 pt-6 pb-5">
              <h2 className="text-xl font-semibold text-gray-900 text-center">
                Change Password
              </h2>
              <button
                onClick={handleCancel}
                className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
                  <path d="M8 8L16 16M16 8L8 16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Form Fields */}
            <div className="px-8 pb-6 pt-2 space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-[1.5px] focus:ring-1 focus:ring-primary-blue rounded-lg focus:outline-none border-primary-blue transition-colors"
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-[1.5px] focus:ring-1 focus:ring-primary-blue rounded-lg focus:outline-none border-primary-blue transition-colors"
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-[1.5px] rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-blue border-primary-blue transition-colors"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-8 pb-6 pt-2 flex gap-3">
              <button
                onClick={handleChangePassword}
                disabled={loading}
                className="flex-1 py-3 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-base disabled:opacity-50 "
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 py-3 bg-white border-[1.5px] border-primary-blue text-primary-blue font-semibold rounded-lg hover:bg-primary-blue hover:text-white transition-colors text-base disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <PasswordUpdateSuccessModal 
        isOpen={showSuccessModal} 
        onClose={handleSuccessClose} 
      />
    </>
  );
};

export default ChangePasswordModal;
